import React, { useState, useEffect } from "react";
import '../styles/ClientDebtSummary.css'; // Optional, for styling
import Swal from 'sweetalert2';
import { Tabs, Tab, Box, Typography } from '@mui/material';
import { formatCurrencyUSD } from "../utils/format";

const SIFCalculator = () => {

    const [clientid, setClientid] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [data, setData] = useState([]);
    const [accountBalData, setAccountBalData] = useState(null);
    const [selectedDebtID, setSelectedDebtID] = useState(null);

    // New states for payment schedule
    const [offerAmount, setOfferAmount] = useState('');
    const [numPayments, setNumPayments] = useState('');
    const [startDate, setStartDate] = useState('');
    const [processor, setProcessor] = useState('1');
    const [method, setMethod] = useState('1');
    const [payments, setPayments] = useState([]);

    // Check for client-id continuously until it is found
    useEffect(() => {
      const observeChanges = () => {
        const cftidElement = document.getElementById('client-id');
        const dmidElement = document.getElementById('external-id');
        const forthidElement = document.getElementById('contact-id');
        const balanceIdElement = document.getElementById('balance-id');
    
        const getValidClientId = () => {
          const cftid = cftidElement?.innerText.trim();
          const dmid = dmidElement?.innerText.trim();
          const forthid = forthidElement?.innerText.trim();
    
          const ids = [forthid, cftid, dmid];
          return ids.find(id => id && id !== '0' && id !== 0) || 0;
        };
    
        const handleChange = () => {
          const foundClientid = getValidClientId();
    
          if (foundClientid && foundClientid !== '0') {
            setClientid(foundClientid);
    
            if (balanceIdElement) {
              const foundBalance = Number(
                balanceIdElement.innerText.trim().replace('$', '').replace(/,/g, '')
              );
              setAccountBalData(foundBalance);
            }
          }
        };
    
        const observer = new MutationObserver(handleChange);
        const config = { childList: true, subtree: true, characterData: true };
    
        if (cftidElement) observer.observe(cftidElement, config);
        if (dmidElement) observer.observe(dmidElement, config);
        if (forthidElement) observer.observe(forthidElement, config);
    
        return () => {
          observer.disconnect(); // Clean up when component unmounts
        };
      };
    
      observeChanges();
    }, []);
    
    
    
      // Fetch data once the client-id is available
      useEffect(() => {
        if (!clientid || clientid === '0') return; // Do not fetch until clientid is found
        
          const fetchData = async () => {
            try {
              //setLoading(true);
              setIsVisible(true); // Show loading data section
              const [validateRes] = await Promise.all([
                fetch(`https://eonedx7iyopdiyc.m.pipedream.net/api/SIFCalculator/GetAllDebtsByClientID?clientid=${clientid}`)

              ]);
      
              const validateData = await validateRes.json();
             
              setData(validateData);

    
            } catch (err) {
                Swal.fire({
                  icon: "error",
                  title: "We're unable to get the debt summary data.",
                  text: "Kindly contact PDR Support for assistance.",
                  confirmButtonColor: '#A5CC2D', 
                });
            } finally {
            }
      
            setIsVisible(false); // Show loading data section
          };
      
          fetchData();
      }, [clientid]); // Trigger when clientid changes

    // Compute payment schedule
    useEffect(() => {
        if (!offerAmount || !numPayments || !startDate || numPayments <= 0) {
            setPayments([]);
            return;
        }

        const amount = parseFloat(offerAmount);
        const num = parseInt(numPayments);
        const start = new Date(startDate);

        if (isNaN(amount) || amount <= 0) {
            setPayments([]);
            return;
        }

        const paymentAmount = amount / num;
        const processorText = processor === '1' ? 'FORTH Custodial' : 'Other'; // Assuming only one option for now
        const methodText = method === '1' ? 'ACH Debit/Check by Phone' : 'Other';

        const schedule = [];
        let remaining = amount;

        for (let i = 1; i <= num; i++) {
            const paymentDate = new Date(start);
            paymentDate.setMonth(start.getMonth() + (i - 1));

            remaining -= paymentAmount;

            schedule.push({
                number: i,
                rawAmount: parseFloat(paymentAmount.toFixed(2)),
                amount: formatCurrencyUSD(paymentAmount),
                remaining: formatCurrencyUSD(Math.max(0, remaining)),
                date: paymentDate.toISOString().split('T')[0], // YYYY-MM-DD
                processor: processorText,
                method: methodText,
                endingBalance: formatCurrencyUSD(Math.max(0, remaining))
            });
        }

        setPayments(schedule);
    }, [offerAmount, numPayments, startDate, processor, method]);

    const handleAmountChange = (index, newAmount) => {
        const updatedPayments = [...payments];
        const numAmount = parseFloat(parseFloat(newAmount).toFixed(2)) || 0;
        updatedPayments[index].rawAmount = numAmount;
        updatedPayments[index].amount = formatCurrencyUSD(numAmount);

        // Recalculate remaining cumulatively
        let cumulativeRemaining = parseFloat(offerAmount);
        for (let i = 0; i < updatedPayments.length; i++) {
            cumulativeRemaining -= updatedPayments[i].rawAmount;
            updatedPayments[i].remaining = formatCurrencyUSD(Math.max(0, cumulativeRemaining));
            updatedPayments[i].endingBalance = formatCurrencyUSD(Math.max(0, cumulativeRemaining));
        }

        setPayments(updatedPayments);
    };

    return (
    <div className="debt-summary-trans-table">
      <div className="flex-container">
        <div className="column">

            <label className="container-headtitle">Offer Terms</label>
            <select
                className="form-control ddl"
                id="ddl-Creditors"
                value={selectedDebtID || "0"}
                onChange={(e) => setSelectedDebtID(e.target.value)}
            >
                <option value="0">Select Creditor</option>
                {data.map((creditor, index) => (
                    <option key={index} value={creditor.DebtID}>
                        {creditor.CreditorName}{" - DebtID: "} {creditor.DebtID}{" - SettlementID: "} {creditor.SettlementID}
                    </option>
                ))}
            </select>

            {isVisible && (<span className="spinner"></span>)}
            <div className="dashboard">
                <div className="flex-container">
                    <div className="column">
                        <label className="container-title">Original Amount</label>
                        <label
                            className="container-count"
                            style={{
                                 //color: data.Balance < 0? 'red' : 'green',
                                }}
                            >
                            {selectedDebtID && selectedDebtID !== "0"
                                ? formatCurrencyUSD(
                                    Number(
                                        data.find((creditor) => String(creditor.DebtID) === selectedDebtID)?.EnrolledAmount || 0
                                    )
                                    )
                                : ""}
                        </label>
                    </div>
                    <div className="column">
                        <label className="container-title">Current Amount</label>
                        <label 
                            className="container-count" 
                            style={{
                                //color: data.Balance < 0? 'red' : 'green',
                                }}
                            >  
                            {selectedDebtID && selectedDebtID !== "0"
                                ? formatCurrencyUSD(
                                    Number(
                                        data.find((creditor) => String(creditor.DebtID) === selectedDebtID)?.CurrentAmount || 0
                                    )
                                    )
                                : ""}
                        </label>
                    </div>
                    <div className="column">
                        <label className="container-title">Settlement Fee</label>
                        <label 
                            className="container-count"  
                            style={{
                                //color: data.Balance < 0? 'red' : 'green',
                                }}
                            >
                              {selectedDebtID && selectedDebtID !== "0"
                                ? formatCurrencyUSD(
                                    Number(
                                        data.find((creditor) => String(creditor.DebtID) === selectedDebtID)?.SettlementFee || 0
                                    )
                                    )
                                : ""}
                        </label>
                    </div>
                    <div className="column">
                        <label className="container-title">Offer Total Savings</label>
                        <label 
                            className="container-count"  
                            style={{
                                //color: data.Balance < 0? 'red' : 'green',
                                }}
                            >
                            {/* {formatCurrencyUSD(Number(totalAverageAmount.toFixed(2)))} */}
                        </label>
                    </div>
                </div>
            </div>   
            <br/>
            <hr/>
            <div className="dashboard">
                <div className="flex-container">
                   <div className="column column-custom2">
                        <label>Offer Amount</label>
                        <input
                            type="number"
                            className="form-control"
                            value={offerAmount}
                            onChange={(e) => setOfferAmount(e.target.value)}
                        />
                    </div>
                    <div className="column column-custom2">
                        <label>Offer %</label>
                        <input
                            type="number"
                            className="form-control"
                        />
                    </div>
                    <div className="column column-custom2">
                        <label>Offer Made By</label>
                        <select 
                            style={{height:'45px', fontSize: '1em'}}
                            className="form-control">
                                <option>Offer By 1</option>
                                <option>Offer By 2</option>
                        </select>
                    </div>
                    <div className="column column-custom2">
                        <label>Negotiator</label>
                        <select 
                            style={{height:'45px',  fontSize: '1em'}}
                            className="form-control">
                                <option>Negotiator 1</option>
                                <option>Negotiator 2</option>
                        </select>
                    </div>
                    <div className="column column-custom2">
                        <label>Valid Until Date</label>
                        <input
                            type="date"
                            className="form-control"
                        />
                    </div>
                   
                </div>
            </div>    

            <hr/><br/>
            <div className="dashboard">
                <label className="container-headtitle">Payment Schedule</label>
                <br/><br/>
                <div className="flex-container">
                    <div className="column column-custom1">
                       <div className="option-group">
                            <label>Start Date:</label>
                            <input
                                type="date"
                                className="form-control"
                                id="txt-start-date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="option-group">
                            <label># of Payments:</label>
                            <input
                                type="number"
                                className="form-control"
                                id="txt-payment-count"
                                value={numPayments}
                                onChange={(e) => setNumPayments(e.target.value)}
                            />
                        </div>
                        <div className="option-group">
                            <label>Processor:</label>
                            <select id ="ddl-processor"
                                style={{height:'45px', width: '100%', fontSize: '1em'}}
                                className="form-control"
                                value={processor}
                                onChange={(e) => setProcessor(e.target.value)}
                            >
                                    <option value="1">FORTH Custodial</option>
                            </select>
                        </div>
                        <div className="option-group">
                            <label>Method:</label>
                            <select id="ddl-method"
                                style={{height:'45px', width: '100%', fontSize: '1em'}}
                                className="form-control"
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                            >
                                    <option value="1">ACH Debit/Check by Phone</option>
                            </select>
                        </div>
                         {/* <button id="btn-check-terms" className="btn btn-secondary">Check Terms</button><br/><br/> */}
                    </div>
                    <div className="column col-lg">
                        
                        <div className="table-container table-sm">
                            {/* {resultHistoryHeaderTrans.length > 0 ? ( */}
                            <table className="table-bordered scrollable-table">
                                <thead>
                                <tr>                       
                                    <th>#</th>
                                    <th>Amount</th>
                                    <th>Remaining</th>
                                    <th>Date</th>
                                    <th>Processor</th>
                                    <th>Method</th>
                                    <th>Ending Balance</th>
                                </tr>
                                </thead>
                                <tbody>
                                    {payments.length > 0 ? (
                                        payments.map((payment, index) => (
                                            <tr key={index}>
                                                <td>{payment.number}</td>
                                                <td style={{width: '100px', fontSize: '1em'}}>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={payment.rawAmount}
                                                        onChange={(e) => handleAmountChange(index, e.target.value)}
                                                    />
                                                </td>
                                                <td>{payment.remaining}</td>
                                                <td>{payment.date}</td>
                                                <td>{payment.processor}</td>
                                                <td>{payment.method}</td>
                                                <td>{payment.endingBalance}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7">No payments to display. Please enter offer amount, number of payments, and start date.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {/* ) : (
                            <p>No data available</p>
                            )} */}
                        </div>
                    </div>
                </div>
            </div>

            

        </div> 
      </div> 
    </div> 
    )
};

export default SIFCalculator;
